import React, { useState } from 'react';
import PublicPageLayout from '../components/landing-page/PublicPageLayout';
import { DemoFormFeedback } from '../components/DemoFormFeedback';
import { usePublicFormSubmit } from '../hooks/usePublicFormSubmit';

const SUPPORT_EMAIL = 'support@pricingtable.ai';

export default function SupportPage() {
  const { status, runSubmit, isSubmitting } = usePublicFormSubmit();
  const [form, setForm] = useState({
    name: '',
    email: '',
    topic: '',
    message: '',
  });

  const onChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const ok = await runSubmit('/public/support', {
      name: form.name,
      email: form.email,
      topic: form.topic,
      message: form.message,
    });
    if (ok) setForm({ name: '', email: '', topic: '', message: '' });
  };

  return (
    <PublicPageLayout title='Support' subtitle='Send us your question and we will follow up.'>
      <form
        onSubmit={onSubmit}
        className='max-w-2xl space-y-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-gray-900/5'
      >
        <div>
          <label htmlFor='support-name' className='mb-2 block text-sm font-medium text-gray-700'>
            Name
          </label>
          <input
            id='support-name'
            required
            autoComplete='name'
            value={form.name}
            onChange={(e) => onChange('name', e.target.value)}
            className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500'
          />
        </div>
        <div>
          <label htmlFor='support-email' className='mb-2 block text-sm font-medium text-gray-700'>
            Email
          </label>
          <input
            id='support-email'
            type='email'
            required
            autoComplete='email'
            value={form.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder='The email associated with your account'
            className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500'
          />
        </div>
        <div>
          <label htmlFor='support-topic' className='mb-2 block text-sm font-medium text-gray-700'>
            Topic
          </label>
          <input
            id='support-topic'
            required
            value={form.topic}
            onChange={(e) => onChange('topic', e.target.value)}
            className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500'
            placeholder='Setup, pricing logic, analytics, or billing'
          />
        </div>
        <div>
          <label htmlFor='support-message' className='mb-2 block text-sm font-medium text-gray-700'>
            Message
          </label>
          <textarea
            id='support-message'
            required
            rows={5}
            value={form.message}
            onChange={(e) => onChange('message', e.target.value)}
            className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500'
          />
        </div>
        <button
          type='submit'
          disabled={isSubmitting}
          className='rounded-md bg-yellow-500 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-70'
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
        <DemoFormFeedback
          tone='light'
          status={status}
          successMessage='Thanks. Your support request has been submitted.'
          errorMessage='Something went wrong. Please try again or email us directly.'
        />
        <p className='text-sm text-gray-600'>
          You can also email us at{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className='font-medium text-yellow-700 underline-offset-2 hover:underline dark:text-yellow-600'
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </form>
    </PublicPageLayout>
  );
}
